import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  CalendarCheck2,
  CalendarX2,
  ChefHat,
  ClipboardList,
  Home,
  LayoutGrid,
  ScrollText,
  ShoppingBag,
  Truck,
  Utensils,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useLocale } from "@/contexts/LocaleProvider";
import {
  can,
  canUseWaiterScreen,
  canViewKitchenOrders,
  isOnlineFeatureEnabled,
} from "@/lib/permissions";
import api from "@/lib/api";
import { cn } from "@/lib/utils";
import "@/styles/simple-ui.css";

const SHORTCUTS = [
  {
    id: "pos",
    to: "/pos",
    labelKey: "nav.pointOfSale",
    hintKey: "simple.tilePosHint",
    icon: ShoppingBag,
    bg: "bg-[#F97316]",
    show: (p) => can(p, "pos.view"),
  },
  {
    id: "tables",
    to: "/tables",
    labelKey: "nav.tableManagement",
    hintKey: "simple.tileTablesHint",
    icon: LayoutGrid,
    bg: "bg-[#2563EB]",
    show: (p) => can(p, "pos.view"),
  },
  {
    id: "kitchen",
    to: "/kds",
    labelKey: "nav.kitchenOrders",
    hintKey: "simple.tileKitchenHint",
    icon: ChefHat,
    bg: "bg-[#059669]",
    show: (p) => canViewKitchenOrders(p),
  },
  {
    id: "delivery",
    to: "/delivery",
    labelKey: "nav.delivery",
    hintKey: "simple.tileDeliveryHint",
    icon: Truck,
    bg: "bg-[#0D9488]",
    show: (p) => can(p, "delivery.view"),
  },
  {
    id: "waiter",
    to: "/waiter",
    labelKey: "nav.waiterOrders",
    hintKey: "simple.tileWaiterHint",
    icon: Utensils,
    bg: "bg-[#7C3AED]",
    show: (p) => canUseWaiterScreen(p),
  },
  {
    id: "online",
    to: "/online/orders",
    labelKey: "nav.onlineOrders",
    hintKey: "simple.tileOnlineHint",
    icon: ClipboardList,
    bg: "bg-[#DC2626]",
    show: (p, user) => can(p, "online.orders.view") && isOnlineFeatureEnabled(user),
  },
  {
    id: "x-report",
    to: "/reports/x-report",
    labelKey: "nav.xReport",
    hintKey: "simple.reportsXHint",
    icon: ScrollText,
    bg: "bg-[#EA580C]",
    show: (p) => can(p, "reports.view"),
  },
];

export default function SimpleOperationsHub() {
  const { t } = useTranslation();
  const { isRtl } = useLocale();
  const { user } = useAuth();
  const permissions = user?.permissions || [];
  const branchId = user?.default_branch?.id;
  const canPos = can(permissions, "pos.view");
  const canReports = can(permissions, "reports.view");

  const { data: businessDayPayload, isLoading: dayLoading } = useQuery({
    queryKey: ["business-day", branchId],
    queryFn: async () => {
      const { data } = await api.get("/business-day/current", { params: { branch_id: branchId } });
      return data;
    },
    enabled: Boolean(branchId && (canPos || canReports)),
    staleTime: 15_000,
    refetchOnMount: "always",
  });
  const businessDay = businessDayPayload?.business_day;
  const dayIsOpen = businessDay?.status === "open";

  const shortcuts = useMemo(
    () => SHORTCUTS.filter((tile) => tile.show(permissions, user)),
    [permissions, user],
  );

  return (
    <div className="simple-ui flex h-full flex-col" dir={isRtl ? "rtl" : "ltr"} data-testid="simple-operations-hub">
      <div className="simple-header shrink-0 px-3 py-3 shadow-md sm:px-4">
        <div className="mx-auto flex max-w-5xl items-center gap-2 sm:gap-3">
          <Link
            to="/"
            className="inline-flex h-11 min-w-[44px] items-center gap-1.5 rounded-xl bg-white/15 px-3 text-sm font-bold text-white hover:bg-white/25"
          >
            <Home className="h-4 w-4" />
            <span className="hidden sm:inline">{t("common.home")}</span>
          </Link>
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-lg font-extrabold text-white sm:text-xl">
              {t("nav.operations")}
            </h1>
            <p className="truncate text-[12px] text-white/80">
              {t("simple.operationsSubtitle", "Day open / close and floor shortcuts")}
            </p>
          </div>
          {(canPos || canReports) && branchId ? (
            <div
              className={cn(
                "shrink-0 rounded-full px-3 py-1.5 text-[12px] font-bold",
                dayLoading
                  ? "bg-white/20 text-white"
                  : dayIsOpen
                    ? "bg-emerald-400/25 text-emerald-100 ring-1 ring-emerald-300/40"
                    : "bg-amber-400/20 text-amber-100 ring-1 ring-amber-300/40",
              )}
              data-testid="ops-day-status"
            >
              {dayLoading
                ? "…"
                : dayIsOpen
                  ? t("simple.dayStatusOpen", { date: businessDay?.business_date || "", defaultValue: `Open · ${businessDay?.business_date || ""}` })
                  : t("simple.dayStatusClosed", "Day closed")}
            </div>
          ) : null}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-5">
        <div className="mx-auto max-w-5xl space-y-6">
          {(canPos || canReports) && branchId ? (
            <section data-testid="simple-day-actions">
              <h2 className="mb-3 text-[12px] font-extrabold uppercase tracking-wider text-slate-500">
                {t("simple.dayActionsTitle", "Business day")}
              </h2>
              <p className="mb-3 text-[13px] text-slate-500">
                {t(
                  "simple.dayActionsHint",
                  "Kept here on purpose — open or close only when you mean to.",
                )}
              </p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {canPos ? (
                  <Link
                    to="/pos"
                    className={cn(
                      "simple-tile simple-tile--action flex items-center gap-4 p-4 text-white sm:p-5",
                      dayIsOpen ? "bg-[#059669]" : "bg-[#F97316]",
                    )}
                    data-testid="simple-day-open"
                  >
                    <span className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-white/20 sm:h-16 sm:w-16">
                      <CalendarCheck2 className="h-7 w-7 sm:h-8 sm:w-8" strokeWidth={2.25} />
                    </span>
                    <span className="min-w-0 text-start">
                      <span className="block text-[18px] font-extrabold leading-tight sm:text-[20px]">
                        {t("simple.dayOpen", "Day Open")}
                      </span>
                      <span className="mt-0.5 block text-[13px] font-medium text-white/90">
                        {dayIsOpen
                          ? t("simple.dayOpenActive", {
                              date: businessDay?.business_date || "",
                              defaultValue: `Open · ${businessDay?.business_date || ""} · go to POS`,
                            })
                          : t("simple.dayOpenHint", "Opens the day, then POS billing")}
                      </span>
                    </span>
                  </Link>
                ) : null}
                {canReports ? (
                  <Link
                    to="/reports/z-report"
                    className={cn(
                      "simple-tile simple-tile--action flex items-center gap-4 p-4 text-white sm:p-5",
                      dayIsOpen ? "bg-[#DC2626]" : "bg-slate-400",
                    )}
                    data-testid="simple-day-close"
                  >
                    <span className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-white/20 sm:h-16 sm:w-16">
                      <CalendarX2 className="h-7 w-7 sm:h-8 sm:w-8" strokeWidth={2.25} />
                    </span>
                    <span className="min-w-0 text-start">
                      <span className="block text-[18px] font-extrabold leading-tight sm:text-[20px]">
                        {t("simple.dayClose", "Day Close")}
                      </span>
                      <span className="mt-0.5 block text-[13px] font-medium text-white/90">
                        {dayIsOpen
                          ? t("simple.dayCloseHint", "Z-Report · end of day")
                          : t("simple.dayAlreadyClosed", "No open day — open first")}
                      </span>
                    </span>
                  </Link>
                ) : null}
              </div>
            </section>
          ) : null}

          {shortcuts.length > 0 ? (
            <section>
              <h2 className="mb-3 text-[12px] font-extrabold uppercase tracking-wider text-slate-500">
                {t("simple.operationsShortcuts", "Floor shortcuts")}
              </h2>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                {shortcuts.map((tile) => {
                  const Icon = tile.icon;
                  return (
                    <Link
                      key={tile.id}
                      to={tile.to}
                      className={cn(
                        "simple-tile flex min-h-[112px] flex-col items-center justify-center gap-2 p-4 text-center text-white",
                        tile.bg,
                      )}
                      data-testid={`ops-shortcut-${tile.id}`}
                    >
                      <Icon className="h-8 w-8" strokeWidth={2.25} />
                      <span className="text-[14px] font-extrabold leading-tight">{t(tile.labelKey)}</span>
                      <span className="text-[11px] font-medium text-white/85">{t(tile.hintKey)}</span>
                    </Link>
                  );
                })}
              </div>
            </section>
          ) : null}
        </div>
      </div>
    </div>
  );
}
